import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { FeatureCard } from '@/client/components/marketing/FeatureCard'
import { TechStackGrid, techStackItems } from '@/client/components/marketing/TechStackGrid'
import { totalMcpTools, totalSdkMethods } from '@/client/constants/marketing-data'
import {
  Users,
  Building2,
  Target,
  MessageSquare,
  Shield,
  Zap,
  ArrowRight,
  Globe,
  Wrench,
  LifeBuoy,
  Inbox,
  BookOpen,
  Bot,
  Mic,
  Code2,
  CheckCircle,
} from 'lucide-react'

const features = [
  {
    icon: Users,
    title: 'Contact Management',
    description: 'Track contacts with full activity history, notes, and relationship mapping to companies.',
  },
  {
    icon: Building2,
    title: 'Company Management',
    description: 'Manage companies with linked contacts, deals, and comprehensive notes.',
  },
  {
    icon: Target,
    title: 'Deal Pipeline',
    description: 'Visual deal stages with drag-and-drop, value tracking, and progress monitoring.',
  },
  {
    icon: LifeBuoy,
    title: 'Cases & Support',
    description: 'Ticket management with status tracking, priority levels, and assignment workflows.',
  },
  {
    icon: Inbox,
    title: 'Lead Capture',
    description: 'Public enquiry forms with status workflow, source tracking, and team assignment.',
  },
  {
    icon: BookOpen,
    title: 'Documentation Wiki',
    description: 'Hierarchical docs with versioning, public/private visibility, and rich text editing.',
  },
  {
    icon: MessageSquare,
    title: 'Threaded Notes',
    description: 'Rich notes with threading support, linked across contacts, companies, deals, and cases.',
  },
  {
    icon: Bot,
    title: 'AI Integration',
    description: '7 MCP servers with 53+ tools for AI agents, voice assistants, and automation.',
  },
  {
    icon: Shield,
    title: 'Secure Authentication',
    description: 'Email/password and Google OAuth with enterprise-grade session management.',
  },
  {
    icon: Zap,
    title: 'Edge Performance',
    description: 'Deployed on Cloudflare Workers for sub-50ms response times globally.',
  },
]

export function LandingPage() {
  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="relative overflow-hidden border-b border-border">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-background" />

        <div className="container relative mx-auto max-w-6xl px-4 py-24 md:py-32">
          <div className="flex flex-col items-center text-center">
            <Badge variant="secondary" className="mb-4">
              AI-Powered CRM Platform
            </Badge>

            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl lg:text-7xl max-w-4xl">
              Manage your business{' '}
              <span className="text-primary">relationships smarter</span>
            </h1>

            <p className="mt-6 text-lg text-muted-foreground max-w-2xl md:text-xl">
              A complete CRM platform with contacts, companies, deals, and AI automation.
              Built for Australian SMEs by Jezweb.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Button size="lg" asChild>
                <Link to="/sign-up">
                  Try L2Stack Free
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/features">
                  <CheckCircle className="mr-2 h-4 w-4" />
                  See All Features
                </Link>
              </Button>
            </div>

            <div className="mt-8 flex items-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-green-500" />
                No Setup Required
              </div>
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-blue-500" />
                AI-Powered
              </div>
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-orange-500" />
                Australian Built
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              Everything you need to manage relationships
            </h2>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
              A complete CRM solution with contacts, companies, deals, cases, documentation,
              and AI integration - all in one platform.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {features.map((feature) => (
              <FeatureCard key={feature.title} {...feature} />
            ))}
          </div>

          <div className="mt-12 text-center">
            <Button variant="outline" size="lg" asChild>
              <Link to="/features">
                Explore All Features
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* AI & Automation Section */}
      <section className="py-24">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="text-center mb-16">
            <Badge variant="secondary" className="mb-4">AI-Ready Platform</Badge>
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              Built for the age of AI agents
            </h2>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
              Connect AI assistants, voice agents, and custom integrations via Model Context Protocol (MCP).
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {/* MCP Servers Card */}
            <Card className="relative overflow-hidden">
              <CardContent className="pt-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary mb-4">
                  <Bot className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-semibold mb-2">7 MCP Servers</h3>
                <p className="text-3xl font-bold text-primary mb-2">{totalMcpTools}+ Tools</p>
                <p className="text-muted-foreground text-sm">
                  Full CRM access via JSON-RPC for Claude Desktop, Cursor IDE, and custom AI agents.
                </p>
              </CardContent>
            </Card>

            {/* Voice Widget Card */}
            <Card className="relative overflow-hidden">
              <CardContent className="pt-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary mb-4">
                  <Mic className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Voice Integration</h3>
                <p className="text-3xl font-bold text-primary mb-2">ElevenLabs</p>
                <p className="text-muted-foreground text-sm">
                  Built-in voice widget for conversational AI on your website with real-time CRM access.
                </p>
              </CardContent>
            </Card>

            {/* TypeScript SDK Card */}
            <Card className="relative overflow-hidden">
              <CardContent className="pt-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary mb-4">
                  <Code2 className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-semibold mb-2">TypeScript SDK</h3>
                <p className="text-3xl font-bold text-primary mb-2">{totalSdkMethods} Methods</p>
                <p className="text-muted-foreground text-sm">
                  Fully typed SDK with 6 module clients for programmatic CRM automation and scripting.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="mt-12 text-center">
            <Button variant="outline" size="lg" asChild>
              <Link to="/technology">
                See Our Technology
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Tech Stack Section */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-4">Powered By</Badge>
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              Modern tech stack, zero compromise
            </h2>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
              Built with the latest technologies for performance, reliability, and security.
            </p>
          </div>

          <TechStackGrid items={techStackItems} />

          <div className="mt-12 text-center">
            <Button variant="outline" size="lg" asChild>
              <Link to="/technology">
                Learn About Our Stack
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              Get started your way
            </h2>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
              Use L2Stack as a self-service platform or work with Jezweb for custom solutions.
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-2">
            {/* Self Service */}
            <div className="relative overflow-hidden rounded-2xl border border-border bg-card p-8">
              <div className="absolute top-4 right-4">
                <span className="inline-flex items-center rounded-full bg-amber-500/10 px-3 py-1 text-xs font-medium text-amber-600 dark:text-amber-400 ring-1 ring-inset ring-amber-500/20">
                  Coming Soon
                </span>
              </div>
              <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10 text-primary mb-6">
                <Zap className="h-7 w-7" />
              </div>
              <h3 className="text-2xl font-bold mb-3">Self-Service</h3>
              <p className="text-muted-foreground mb-6">
                Sign up and start using L2Stack immediately. Perfect for small teams and startups.
              </p>
              <ul className="space-y-3 mb-8 text-sm">
                <li className="flex items-center gap-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                  Instant access to all features
                </li>
                <li className="flex items-center gap-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                  AI tools and automation
                </li>
                <li className="flex items-center gap-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                  Email and chat support
                </li>
                <li className="flex items-center gap-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                  Free tier available
                </li>
              </ul>
              <Button variant="outline" disabled className="cursor-not-allowed opacity-60">
                <ArrowRight className="mr-2 h-4 w-4" />
                Coming Soon
              </Button>
            </div>

            {/* Managed Service */}
            <div className="relative overflow-hidden rounded-2xl border border-border bg-card p-8">
              <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10 text-primary mb-6">
                <Wrench className="h-7 w-7" />
              </div>
              <h3 className="text-2xl font-bold mb-3">Managed Service</h3>
              <p className="text-muted-foreground mb-6">
                Let Jezweb handle everything. Custom setup, branding, and dedicated support.
              </p>
              <ul className="space-y-3 mb-8 text-sm">
                <li className="flex items-center gap-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                  Custom branding & domain
                </li>
                <li className="flex items-center gap-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                  Voice assistant integration
                </li>
                <li className="flex items-center gap-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                  Dedicated account manager
                </li>
                <li className="flex items-center gap-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                  Custom development
                </li>
              </ul>
              <Button asChild>
                <Link to="/services">
                  <Globe className="mr-2 h-4 w-4" />
                  Explore Services
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto max-w-4xl px-4 text-center">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4">
            Ready to manage your business smarter?
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Join Australian businesses using L2Stack to manage contacts, close deals, and automate workflows.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild>
              <Link to="/sign-up">
                Start Free Trial
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/contact">
                Contact Sales
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  )
}
